import { useEffect, useState, useCallback } from "react";
import api, { formatErr } from "../../lib/api";
import { toast } from "sonner";
import { Eye, ShoppingCart, Heart, CheckCircle2, ArrowUpRight, Users, X, Mail, Phone, CreditCard, Send } from "lucide-react";
import { formatPrice } from "../../lib/format";

const RANGES = [
  { id: "24h", hours: 24, label: "24 hours" },
  { id: "7d", hours: 168, label: "7 days" },
  { id: "30d", hours: 720, label: "30 days" },
  { id: "90d", hours: 2160, label: "90 days" },
];

// Human-readable copy for each funnel event
const STAGE_META = {
  product_view: { title: "Viewed but didn't add to cart", icon: Eye },
  add_to_cart: { title: "Added to cart · didn't buy", icon: ShoppingCart },
  wishlist_add: { title: "Wishlisted · didn't buy", icon: Heart },
  checkout_started: { title: "Started checkout · didn't pay", icon: CreditCard },
  purchased: { title: "Purchased this piece", icon: CheckCircle2 },
};

export default function EngagementAdmin() {
  const [range, setRange] = useState("7d");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sortBy, setSortBy] = useState("views");
  const [drawer, setDrawer] = useState(null); // { product, event, users, loading, anon }

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const hours = RANGES.find(r => r.id === range).hours;
      const { data } = await api.get(`/admin/products/engagement?hours=${hours}`);
      setData(data);
    } catch (e) { toast.error(formatErr(e)); } finally { setLoading(false); }
  }, [range]);
  useEffect(() => { load(); }, [load]);

  const openStage = async (product, event) => {
    const hours = RANGES.find(r => r.id === range).hours;
    setDrawer({ product, event, users: [], loading: true, anon: 0, admin: 0, adminActors: [] });
    try {
      const url = event === "purchased"
        ? `/admin/products/${product.product_id}/buyers?hours=${hours}`
        : `/admin/products/${product.product_id}/abandoned?event=${event}&hours=${hours}`;
      const { data } = await api.get(url);
      setDrawer({
        product, event, loading: false,
        users: data.users || [],
        anon: data.anonymous_hits || 0,
        admin: data.admin_hits || 0,
        adminActors: data.admin_actors || [],
      });
    } catch (e) {
      toast.error(formatErr(e));
      setDrawer(null);
    }
  };

  const sorted = data?.products ? [...data.products].sort((a, b) => (b[sortBy] || 0) - (a[sortBy] || 0)) : [];
  const totals = sorted.reduce((acc, p) => ({
    views: acc.views + (p.views || 0),
    atc: acc.atc + (p.add_to_cart || 0),
    wishlist: acc.wishlist + (p.wishlist_add || 0),
    checkout: acc.checkout + (p.checkout_started || 0),
    purchased: acc.purchased + (p.purchased || 0),
  }), { views: 0, atc: 0, wishlist: 0, checkout: 0, purchased: 0 });

  return (
    <div className="space-y-8" data-testid="engagement-admin">
      <div className="flex items-end justify-between flex-wrap gap-4 pb-5 border-b border-[rgba(244,244,240,0.05)]">
        <div>
          <p className="text-[10px] uppercase tracking-[0.3em] text-gold mb-2">Atelier</p>
          <h2 className="font-serif text-4xl">Product Engagement</h2>
          <p className="text-xs text-muted mt-2 max-w-2xl leading-relaxed">
            Full funnel per product: viewed → added to cart → wishlisted → started checkout → purchased. <span className="text-gold">Click any number</span> to see the exact customers stuck at that stage (name, email, phone).
          </p>
        </div>
        <div className="flex items-center gap-2 bg-[#141414] p-1 border border-[rgba(244,244,240,0.06)]">
          {RANGES.map(r => (
            <button
              key={r.id}
              onClick={() => setRange(r.id)}
              className={`px-3 py-2 text-[10px] uppercase tracking-[0.22em] transition-colors ${range === r.id ? "bg-gold text-black" : "text-muted hover:text-gold"}`}
              data-testid={`eng-range-${r.id}`}
            >{r.label}</button>
          ))}
        </div>
      </div>

      {/* Totals strip */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3" data-testid="engagement-totals">
        <Stat icon={Eye} label="Viewed" value={totals.views.toLocaleString()} />
        <Stat icon={ShoppingCart} label="Add-to-cart" value={totals.atc.toLocaleString()} />
        <Stat icon={Heart} label="Wishlisted" value={totals.wishlist.toLocaleString()} />
        <Stat icon={CreditCard} label="Started checkout" value={totals.checkout.toLocaleString()} />
        <Stat icon={CheckCircle2} label="Purchased" value={totals.purchased.toLocaleString()} accent />
      </div>

      {/* Table */}
      <div className="bg-[#141414] border border-[rgba(244,244,240,0.05)] overflow-hidden">
        <div className="flex items-center justify-between p-4 border-b border-[rgba(244,244,240,0.05)] flex-wrap gap-3">
          <p className="text-xs uppercase tracking-[0.25em] text-gold">All products ({sorted.length})</p>
          <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.18em] text-muted">
            <span>Sort by</span>
            {["views", "add_to_cart", "checkout_started", "purchased", "conversion_rate"].map(k => (
              <button
                key={k}
                onClick={() => setSortBy(k)}
                className={`px-2 py-1 ${sortBy === k ? "text-gold border border-gold/30" : "hover:text-gold"}`}
                data-testid={`sort-${k}`}
              >{k.replace(/_/g, " ")}</button>
            ))}
          </div>
        </div>
        {loading ? (
          <p className="text-muted p-6 text-sm">Loading engagement…</p>
        ) : sorted.length === 0 ? (
          <p className="text-muted p-6 text-sm">
            No products yet — add pieces from the Products tab to see engagement.
          </p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm min-w-[900px]">
              <thead>
                <tr className="text-[10px] uppercase tracking-[0.18em] text-muted border-b border-[rgba(244,244,240,0.05)]">
                  <th className="text-left p-3 font-normal">Product</th>
                  <th className="text-right p-3 font-normal">Views</th>
                  <th className="text-right p-3 font-normal">Cart</th>
                  <th className="text-right p-3 font-normal">Wishlist</th>
                  <th className="text-right p-3 font-normal">Checkout</th>
                  <th className="text-right p-3 font-normal">Sold</th>
                  <th className="text-right p-3 font-normal">ATC%</th>
                  <th className="text-right p-3 font-normal">Conv%</th>
                  <th className="p-3 font-normal"></th>
                </tr>
              </thead>
              <tbody>
                {sorted.map((p, idx) => (
                  <tr key={p.product_id} className="border-b border-[rgba(244,244,240,0.03)] hover:bg-[#1a1a1a]" data-testid={`eng-row-${p.product_id}`}>
                    <td className="p-3">
                      <div className="flex items-center gap-3 min-w-0">
                        <span className="text-[10px] text-muted w-4 tabular-nums text-right">{idx + 1}</span>
                        {p.image ? <img src={p.image} alt="" className="w-10 h-12 object-cover shrink-0" /> : <div className="w-10 h-12 bg-[#0C0C0C] shrink-0" />}
                        <div className="min-w-0">
                          <p className="font-serif text-sm truncate">{p.name}</p>
                          <p className="text-[10px] uppercase tracking-[0.15em] text-muted mt-0.5">{p.category} · {formatPrice(p.price)}</p>
                        </div>
                      </div>
                    </td>
                    <CountCell product={p} value={p.views} event="product_view" onClick={openStage} testid={`stage-view-${p.product_id}`} />
                    <CountCell product={p} value={p.add_to_cart} event="add_to_cart" onClick={openStage} testid={`stage-cart-${p.product_id}`} />
                    <CountCell product={p} value={p.wishlist_add} event="wishlist_add" onClick={openStage} testid={`stage-wishlist-${p.product_id}`} />
                    <CountCell product={p} value={p.checkout_started} event="checkout_started" onClick={openStage} testid={`stage-checkout-${p.product_id}`} />
                    <CountCell product={p} value={p.purchased} event="purchased" onClick={openStage} testid={`stage-purchased-${p.product_id}`} accent />
                    <td className="p-3 text-right tabular-nums text-muted">{p.atc_rate}%</td>
                    <td className={`p-3 text-right tabular-nums ${p.conversion_rate >= 5 ? "text-gold" : p.conversion_rate > 0 ? "text-text" : "text-muted/60"}`}>{p.conversion_rate}%</td>
                    <td className="p-3">
                      <a href={`/product/${p.product_id}`} target="_blank" rel="noreferrer" className="text-muted hover:text-gold" title="Open product page">
                        <ArrowUpRight size={14} strokeWidth={1.5} />
                      </a>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Stage users drawer */}
      {drawer && (
        <div className="fixed inset-0 z-[90] flex items-stretch" data-testid="abandoned-drawer">
          <div className="absolute inset-0 bg-black/80 backdrop-blur-sm" onClick={() => setDrawer(null)} />
          <div className="relative ml-auto bg-[#0C0C0C] border-l border-[rgba(244,244,240,0.08)] w-full max-w-xl flex flex-col overflow-hidden">
            <div className="flex items-start justify-between p-5 border-b border-[rgba(244,244,240,0.06)]">
              <div className="min-w-0">
                <p className="text-[10px] uppercase tracking-[0.25em] text-gold mb-1 flex items-center gap-1.5">
                  {(() => { const Icon = (STAGE_META[drawer.event] || STAGE_META.add_to_cart).icon; return <Icon size={11} strokeWidth={1.5} />; })()}
                  {(STAGE_META[drawer.event] || {}).title || "Customers"}
                </p>
                <h3 className="font-serif text-xl truncate">{drawer.product.name}</h3>
                <p className="text-[10px] uppercase tracking-[0.18em] text-muted mt-1">
                  {formatPrice(drawer.product.price)} · {drawer.users.length} customer{drawer.users.length === 1 ? "" : "s"}
                  {drawer.anon > 0 && <span> · {drawer.anon} anonymous</span>}
                  {drawer.admin > 0 && <span> · {drawer.admin} admin</span>}
                </p>
              </div>
              <button onClick={() => setDrawer(null)} className="text-muted hover:text-gold" data-testid="close-abandoned-drawer"><X size={20} strokeWidth={1.5} /></button>
            </div>
            <div className="flex-1 overflow-y-auto p-5 space-y-5">
              {drawer.loading ? (
                <p className="text-muted text-sm">Loading customers…</p>
              ) : (
                <>
                  {drawer.users.length === 0 ? (
                    <div className="text-muted text-sm space-y-3 bg-[#141414] p-4 border border-[rgba(244,244,240,0.06)]">
                      <p className="text-text">No <span className="text-gold">registered customers</span> at this stage yet.</p>
                      {drawer.anon > 0 && (
                        <p className="text-xs">
                          <span className="text-gold font-medium">{drawer.anon}</span> anonymous visitor{drawer.anon === 1 ? "" : "s"} triggered this action but weren't signed in — encourage sign-up at checkout to unlock recovery emails for them.
                        </p>
                      )}
                      {drawer.admin > 0 && (
                        <p className="text-xs">
                          <span className="text-gold font-medium">{drawer.admin}</span> admin/staff hit{drawer.admin === 1 ? "" : "s"} shown below — these are test actions, not real customers.
                        </p>
                      )}
                      {drawer.anon === 0 && drawer.admin === 0 && (
                        <p className="text-xs">Once a signed-in shopper hits this stage, they'll appear here with contact info.</p>
                      )}
                    </div>
                  ) : (
                    <>
                      <SegmentBroadcastComposer users={drawer.users} product={drawer.product} event={drawer.event} />
                      <ul className="space-y-3" data-testid="abandoned-user-list">
                        {drawer.users.map(u => (
                          <li key={u.user_id} className="bg-[#141414] p-4 border border-[rgba(244,244,240,0.06)]" data-testid={`abandoned-user-${u.user_id}`}>
                            <div className="flex items-start justify-between gap-3">
                              <div className="min-w-0">
                                <p className="font-serif text-base truncate">{u.name || "—"}</p>
                                <p className="text-xs text-muted flex items-center gap-1.5 mt-1"><Mail size={10} strokeWidth={1.5} /> <a href={`mailto:${u.email}`} className="hover:text-gold truncate">{u.email}</a></p>
                                {u.phone && <p className="text-xs text-muted flex items-center gap-1.5 mt-0.5"><Phone size={10} strokeWidth={1.5} /> <a href={`tel:+91${u.phone}`} className="hover:text-gold">{u.phone}</a></p>}
                              </div>
                              <div className="text-right shrink-0">
                                <p className="text-[10px] uppercase tracking-[0.18em] text-gold">{u.activity_count}×</p>
                                <p className="text-[10px] text-muted mt-0.5">{u.last_activity_at ? new Date(u.last_activity_at).toLocaleDateString() : ""}</p>
                              </div>
                            </div>
                          </li>
                        ))}
                      </ul>
                    </>
                  )}

                  {drawer.adminActors && drawer.adminActors.length > 0 && (
                    <details className="bg-[#0C0C0C] border border-gold/20 p-3" data-testid="admin-actors-details">
                      <summary className="cursor-pointer text-[10px] uppercase tracking-[0.22em] text-gold flex items-center gap-2">
                        <Users size={11} strokeWidth={1.5} /> Also seen: {drawer.adminActors.length} admin/staff test action{drawer.adminActors.length === 1 ? "" : "s"}
                      </summary>
                      <ul className="mt-3 space-y-2">
                        {drawer.adminActors.map(u => (
                          <li key={u.user_id} className="text-xs text-muted flex items-center justify-between" data-testid={`admin-actor-${u.user_id}`}>
                            <span>{u.email} <span className="text-muted/60">· {u.name}</span></span>
                            <span className="text-gold">{u.activity_count}× · {u.last_activity_at ? new Date(u.last_activity_at).toLocaleDateString() : ""}</span>
                          </li>
                        ))}
                      </ul>
                      <p className="text-[10px] text-muted/70 mt-2">These are admin accounts (like yours) — filtered out of the customer list because they're not real shoppers.</p>
                    </details>
                  )}
                </>
              )}
            </div>
            <div className="p-4 border-t border-[rgba(244,244,240,0.06)] text-[10px] uppercase tracking-[0.18em] text-muted text-center">
              {drawer.event === "add_to_cart" || drawer.event === "wishlist_add"
                ? "Automated recovery emails go out daily at 09:00 UTC · manage templates in Email Templates"
                : "Tip: use Broadcast Email to send this segment a targeted offer"}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function CountCell({ product, value, event, onClick, testid, accent }) {
  const v = value || 0;
  const cls = `p-3 text-right tabular-nums ${accent ? "text-gold" : ""}`;
  if (v === 0) {
    return <td className={`${cls} text-muted/60`}>0</td>;
  }
  return (
    <td className={cls}>
      <button
        onClick={() => onClick(product, event)}
        className={`${accent ? "text-gold" : "text-text"} hover:text-gold underline decoration-dotted underline-offset-4`}
        data-testid={testid}
        title="Click to see which customers are at this stage"
      >{v}</button>
    </td>
  );
}

function Stat({ icon: Icon, label, value, accent }) {
  return (
    <div className={`p-4 border ${accent ? "bg-gold/5 border-gold/30" : "bg-[#141414] border-[rgba(244,244,240,0.05)]"}`}>
      <div className="flex items-center gap-2 mb-2">
        <Icon size={12} strokeWidth={1.5} className={accent ? "text-gold" : "text-muted"} />
        <p className="text-[10px] uppercase tracking-[0.22em] text-muted">{label}</p>
      </div>
      <p className={`font-serif text-3xl leading-none ${accent ? "text-gold" : "text-text"}`}>{value}</p>
    </div>
  );
}

const STAGE_COPY = {
  product_view: {
    subject: "A piece from GOSHI is waiting for you",
    body: "<p>Dear {{customer_first_name}},</p><p>You had your eye on <strong>{{PRODUCT_NAME}}</strong> — it's still available and we've reserved a piece for you.</p><p><a href=\"{{PRODUCT_URL}}\" style=\"color:#C8A97E;\">See the piece →</a></p><p>Use code <strong>WELCOME10</strong> for 10% off your first order.</p>",
  },
  add_to_cart: {
    subject: "You left {{PRODUCT_NAME}} in your cart",
    body: "<p>Dear {{customer_first_name}},</p><p>The piece you added to cart — <strong>{{PRODUCT_NAME}}</strong> — is still waiting. Complete your order in one tap.</p><p><a href=\"{{PRODUCT_URL}}\" style=\"color:#C8A97E;\">Return to cart →</a></p>",
  },
  wishlist_add: {
    subject: "{{PRODUCT_NAME}} — still available",
    body: "<p>Dear {{customer_first_name}},</p><p>You wishlisted <strong>{{PRODUCT_NAME}}</strong>. It's still in stock, but our editions are limited.</p><p><a href=\"{{PRODUCT_URL}}\" style=\"color:#C8A97E;\">Reserve yours →</a></p>",
  },
  checkout_started: {
    subject: "You were so close · complete your GOSHI order",
    body: "<p>Dear {{customer_first_name}},</p><p>You started checkout for <strong>{{PRODUCT_NAME}}</strong> but didn't finish. Here's <strong>10% off</strong> to help you decide — use code <strong>CHECKOUT10</strong> at checkout.</p><p><a href=\"{{PRODUCT_URL}}\" style=\"color:#C8A97E;\">Complete your order →</a></p>",
  },
  purchased: {
    subject: "A note from GOSHI",
    body: "<p>Dear {{customer_first_name}},</p><p>Thank you for choosing <strong>{{PRODUCT_NAME}}</strong>. We hope you love it.</p>",
  },
};

function SegmentBroadcastComposer({ users, product, event }) {
  const [open, setOpen] = useState(false);
  const preset = STAGE_COPY[event] || STAGE_COPY.add_to_cart;
  const productUrl = `${window.location.origin}/product/${product.product_id}`;
  const fillTokens = (s) => (s || "").replaceAll("{{PRODUCT_NAME}}", product.name || "").replaceAll("{{PRODUCT_URL}}", productUrl);
  const [subject, setSubject] = useState(fillTokens(preset.subject));
  const [body, setBody] = useState(fillTokens(preset.body));
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const send = async () => {
    if (!confirm(`Send this email to ${users.length} customer${users.length === 1 ? "" : "s"}?`)) return;
    setBusy(true); setResult(null);
    try {
      const { data } = await api.post("/admin/broadcasts/segment", {
        subject: subject.trim(),
        body_html: body,
        user_ids: users.map(u => u.user_id),
      });
      setResult(data);
      toast.success(`Sent to ${data.sent} customer${data.sent === 1 ? "" : "s"}${data.failure_count ? ` · ${data.failure_count} failed` : ""}`, { duration: 6000 });
    } catch (e) { toast.error(formatErr(e), { duration: 8000 }); } finally { setBusy(false); }
  };

  if (!open) {
    return (
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="w-full bg-gold/10 border border-gold/30 text-gold px-4 py-3 flex items-center gap-3 hover:bg-gold/15 transition-colors"
        data-testid="open-segment-broadcast-btn"
      >
        <Send size={13} strokeWidth={1.5} />
        <div className="flex-1 text-left">
          <p className="text-[10px] uppercase tracking-[0.22em]">Send targeted email to these {users.length}</p>
          <p className="text-xs text-muted mt-0.5">Recover this segment with a pre-drafted, personalised email</p>
        </div>
        <span className="text-[10px] uppercase tracking-[0.18em]">Compose →</span>
      </button>
    );
  }

  if (result) {
    return (
      <div className="bg-gold/10 border border-gold/30 p-4" data-testid="segment-broadcast-sent">
        <p className="text-[10px] uppercase tracking-[0.22em] text-gold mb-1">Broadcast sent</p>
        <p className="text-xs text-muted">
          <span className="text-gold">{result.sent}</span> delivered
          {result.failure_count ? <span> · <span className="text-red-400">{result.failure_count}</span> failed</span> : null}
        </p>
        <button onClick={() => { setResult(null); setOpen(false); }} className="btn-outline !px-3 !py-1.5 text-[10px] mt-3">Done</button>
      </div>
    );
  }

  return (
    <div className="bg-[#0C0C0C] border border-gold/20 p-4 space-y-3" data-testid="segment-broadcast-composer">
      <div className="flex items-center justify-between">
        <p className="text-[10px] uppercase tracking-[0.22em] text-gold flex items-center gap-1.5"><Send size={11} strokeWidth={1.5} /> Targeted email · {users.length} recipient{users.length === 1 ? "" : "s"}</p>
        <button onClick={() => setOpen(false)} className="text-muted hover:text-gold text-[10px] uppercase tracking-[0.18em]" data-testid="close-segment-composer">Cancel</button>
      </div>
      <div>
        <label className="text-[10px] uppercase tracking-[0.22em] text-muted block mb-1.5">Subject</label>
        <input className="input-line w-full !py-2" value={subject} onChange={e => setSubject(e.target.value)} data-testid="segment-subject" />
      </div>
      <div>
        <label className="text-[10px] uppercase tracking-[0.22em] text-muted block mb-1.5">Body (HTML supported)</label>
        <textarea rows={7} className="input-line w-full !py-2 font-mono text-xs resize-y" value={body} onChange={e => setBody(e.target.value)} data-testid="segment-body" />
        <p className="text-[10px] text-muted/80 mt-1.5">Tokens: <code className="bg-[#141414] px-1.5 py-0.5">{"{{customer_first_name}}"}</code>, <code className="bg-[#141414] px-1.5 py-0.5">{"{{customer_full_name}}"}</code>, <code className="bg-[#141414] px-1.5 py-0.5">{"{{email}}"}</code></p>
      </div>
      <button onClick={send} disabled={busy || !subject.trim() || !body.trim()} className="btn-gold w-full disabled:opacity-40" data-testid="segment-send-btn">
        {busy ? `Sending to ${users.length}…` : `Send to ${users.length} customer${users.length === 1 ? "" : "s"}`}
      </button>
    </div>
  );
}

